import { faArrowAltCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowAltCircleLeft'
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'
import { faFileCsv } from '@fortawesome/pro-regular-svg-icons/faFileCsv'
import { faUpload } from '@fortawesome/pro-regular-svg-icons/faUpload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import axios from 'axios'
import classNames from 'classnames'
import startCase from 'lodash/startCase'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'

import { GET_LIST, GET_ONE, useClient } from '@peracto/client'
import { useConfig } from '@peracto/peracto-config'
import { Group, Heading, HydraTable, Modal, TimeAgo } from '@peracto/peracto-ui'

import {
    DEFAULT_ERROR_MESSAGE,
    LANDING_PAGES_API_IDENTIFIER,
    POLL_INTERVAL_MS,
    axiosHydraMessage,
    formatLandingPagesDataTypeLabel,
    getLandingPagesImportManagementOption,
    importLogId,
    isCreatedWithinLast24Hours,
} from '../shared'

const landingPagesImportOption = getLandingPagesImportManagementOption(LANDING_PAGES_API_IDENTIFIER)

const LandingPagesManageDataList = () => {
    const { client } = useClient()
    const config = useConfig()
    const apiUrl = config.get('api')
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const fileInputRef = useRef(null)
    const pendingUploadFile = useRef(null)
    const [chosenFileLabel, setChosenFileLabel] = useState('')
    const [hasPendingFile, setHasPendingFile] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [tableKey, setTableKey] = useState(0)
    const [listRows, setListRows] = useState([])

    const clearPendingFile = useCallback(() => {
        pendingUploadFile.current = null
        setChosenFileLabel('')
        setHasPendingFile(false)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [])

    const [selectedColumns, onSelectColumn] = useState({})
    const [guideModalOpen, setGuideModalOpen] = useState(false)

    const shouldPoll = useMemo(
        () =>
            listRows.some(
                (row) => row?.status === 'processing' && isCreatedWithinLast24Hours(row?.createdAt)
            ),
        [listRows]
    )

    const downloadUploadedFile = useCallback(
        async (id) => {
            try {
                const { data: downloadData } = await client(GET_ONE, 'import-logs', {
                    id: `/import-logs/${id}/pre-signed-url`,
                })

                if (downloadData?.downloadUrl) {
                    window.location.href = downloadData.downloadUrl
                } else {
                    toast.error(DEFAULT_ERROR_MESSAGE)
                }
            } catch (e) {
                console.error(e)
                const status = e?.status ?? e?.error?.status ?? e?.response?.status
                const hydraDescription = e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : null

                if (status === 400) {
                    toast.error(
                        hydraDescription ?? 'File is not ready to download yet. Please try again.'
                    )
                } else {
                    toast.error(hydraDescription ?? DEFAULT_ERROR_MESSAGE)
                }
            }
        },
        [client]
    )

    const columns = useMemo(
        () => [
            {
                Header: 'ID',
                id: 'importId',
                accessor: (row) => importLogId(row),
                disableSorting: true,
                Cell: ({ cell: { value }, row }) => (
                    <span data-testid={`row-${row.index}-id`}>{value}</span>
                ),
                selectable: false,
            },
            {
                Header: 'Original file',
                accessor: 'originalFilename',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (value ? value : '-'),
                selectable: false,
            },
            {
                Header: 'Type',
                accessor: 'type',
                sortType: 'basic',
                Cell: ({ cell: { value } }) => formatLandingPagesDataTypeLabel(value),
                selectable: false,
                disableSorting: true,
            },
            {
                Header: 'Status',
                accessor: 'status',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (
                    <span
                        className={classNames('badge', 'badge-pill', {
                            'badge-info': value === 'processing',
                            'badge-success': value === 'complete',
                            'badge-danger': value === 'error',
                        })}
                    >
                        {startCase(value)}
                    </span>
                ),
                selectable: false,
            },
            {
                Header: 'Row Count',
                id: 'rowCount',
                accessor: 'rowCount',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (value != null ? value : '-'),
                selectable: false,
            },
            {
                Header: 'Errors',
                id: 'errorRowCount',
                accessor: 'errorRowCount',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (value != null ? value : '-'),
                selectable: false,
            },
            {
                Header: 'Created',
                accessor: 'createdAt',
                disableSorting: true,
                Cell: ({ cell: { value } }) => {
                    if (!value) return '-'
                    const date = new Date(value)
                    if (!date.getDate()) return '-'
                    return <TimeAgo title={formatDate(date)} date={value} />
                },
                selectable: false,
            },
            {
                id: 'viewImport',
                Header: '',
                accessor: () => null,
                disableSorting: true,
                Cell: ({ row }) => {
                    const id = importLogId(row.original)
                    if (!id) return null
                    return (
                        <Link
                            to={`/manage-landing-pages-data/${id}`}
                            className="p-0 btn btn-sm btn-link text-primary"
                            data-testid={`row-${row.index}-view-import`}
                        >
                            View import
                        </Link>
                    )
                },
                selectable: false,
            },
            {
                Header: 'Actions',
                id: 'actions',
                accessor: () => null,
                disableSorting: true,
                Cell: ({ row }) => {
                    const id = importLogId(row.original)
                    if (!id) return '-'
                    return (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => downloadUploadedFile(id)}
                            data-testid={`row-${row.index}-download`}
                        >
                            <FontAwesomeIcon icon={faDownload} className="mr-2" />
                            Download CSV
                        </button>
                    )
                },
                selectable: false,
            },
        ],
        [formatDate, downloadUploadedFile]
    )

    const onFetchData = useCallback(
        async (options) => {
            const result = await client(GET_LIST, 'import-logs', options)
            setListRows(Array.isArray(result?.data) ? result.data : [])
            return result
        },
        [client]
    )

    useEffect(() => {
        if (!shouldPoll) return undefined

        const id = setInterval(() => {
            setTableKey((k) => k + 1)
        }, POLL_INTERVAL_MS)

        return () => clearInterval(id)
    }, [shouldPoll])

    const handleFileChosen = (e) => {
        const file = e.currentTarget.files?.[0]
        e.target.value = ''
        if (!file) return
        pendingUploadFile.current = file
        setChosenFileLabel(file.name)
        setHasPendingFile(true)
    }

    const handleUploadFile = async () => {
        const file = pendingUploadFile.current
        if (!file) {
            toast.info('Choose a CSV file first.')
            return
        }

        const formData = new FormData()
        formData.append('type', LANDING_PAGES_API_IDENTIFIER)
        formData.append('file', file)

        setUploading(true)
        try {
            await axios.post(`${apiUrl}/import-logs`, formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            })
            toast.success(landingPagesImportOption.uploadSuccessMessage)
            clearPendingFile()
            setTableKey((k) => k + 1)
        } catch (err) {
            console.error(err)
            toast.error(axiosHydraMessage(err) ?? DEFAULT_ERROR_MESSAGE)
        } finally {
            setUploading(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>{`Bulk manage landing pages data - ${landingPagesImportOption.listTitle} | Peracto`}</title>
            </Helmet>
            <Heading name="Bulk Manage Landing Pages Data">
                <div className="flex-wrap ml-2 d-flex align-items-center">
                    <Link className="mb-2 mr-2 btn btn-outline-primary mb-sm-0" to="/content">
                        <FontAwesomeIcon icon={faArrowAltCircleLeft} className="mr-2" />
                        Back to content
                    </Link>
                </div>
            </Heading>

            <Modal
                isVisible={guideModalOpen}
                title="Landing Pages Import Guide"
                className="landing-pages-import-guide-modal"
                close={() => setGuideModalOpen(false)}
                buttons={[
                    {
                        type: 'btn-outline-primary',
                        text: 'Close',
                        action: () => setGuideModalOpen(false),
                    },
                ]}
            >
                <div
                    className="landing-pages-import-guide-modal-body"
                    style={{ maxHeight: '70vh', overflowY: 'auto' }}
                >
                    <p className="text-muted mb-3">
                        Bulk import supports updating landing page content via CSV. Generate a{' '}
                        <strong>Landing Pages Export</strong> to get a file with the correct columns
                        and current values, edit the rows you want to change, then upload it here.
                        Your file must be comma-separated CSV; from Numbers or Excel use{' '}
                        <strong>CSV (UTF-8)</strong>. The header row must match the columns from the
                        export exactly. One landing page per row.
                    </p>
                    <p className="text-muted mb-0">
                        The import columns match the export columns exactly. Use the export as your
                        template.
                    </p>
                </div>
            </Modal>

            <div className="row">
                <div className="col">
                    <Group id="landing-pages-manage-data-upload" name="Manage Data">
                        <div className="mb-4">
                            <p className="mb-0 text-muted">
                                {landingPagesImportOption.instructionsBeforeGuide}
                                <button
                                    type="button"
                                    className="btn btn-link text-primary p-0 align-baseline"
                                    onClick={() => setGuideModalOpen(true)}
                                    data-testid="view-landing-pages-import-guide"
                                >
                                    {landingPagesImportOption.guideLinkLabel}
                                </button>
                                {landingPagesImportOption.instructionsAfterGuide}
                            </p>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="d-none"
                            onChange={handleFileChosen}
                            data-testid="bulk-landing-pages-csv-input"
                        />

                        <div className="flex-wrap mb-3 d-flex align-items-center">
                            <button
                                type="button"
                                className="mb-2 mr-3 btn btn-outline-primary mb-sm-0"
                                disabled={uploading}
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="choose-bulk-landing-pages-csv"
                            >
                                <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                                Choose File
                            </button>
                            <span className="mb-2 text-muted mb-sm-0">
                                {chosenFileLabel || 'No File Selected'}
                            </span>
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={uploading || !hasPendingFile}
                            onClick={handleUploadFile}
                            data-testid="upload-bulk-landing-pages-csv"
                        >
                            <FontAwesomeIcon icon={faUpload} className="mr-2" />
                            {uploading ? 'Uploading...' : 'Upload File'}
                        </button>
                    </Group>

                    <div className="card card--transparent">
                        <div className="card-body">
                            <h2 className="mb-4 h5">
                                {landingPagesImportOption.historyTitle ??
                                    landingPagesImportOption.listTitle}
                            </h2>
                            <HydraTable
                                key={tableKey}
                                columns={columns}
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                                alwaysFilter={{ type: LANDING_PAGES_API_IDENTIFIER }}
                                noResultsMessage={
                                    <p className="mb-0 text-muted">
                                        {landingPagesImportOption.noResultsMessage}
                                    </p>
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default LandingPagesManageDataList